import { Marker } from "@vis.gl/react-maplibre";
import type { FC } from "react";

import { MapLibreAutoPanPopup } from "./maplibre-auto-pan-popup";
import { MapLibrePlaceInfoCard } from "./maplibre-place-info-card";
import { MapLibrePlaceInfoHint } from "./maplibre-place-info-hint";
import { MapLibrePlaceInfoToggle } from "./maplibre-place-info-toggle";
import { MAPLIBRE_PLACE_INFO } from "./maplibre-place-info.constants";
import { MapLibrePlaceInfoPointStyled } from "./maplibre-place-info.styled";
import { useMapLibrePlaceInfo } from "./use-maplibre-place-info";

interface IMapLibrePlaceInfoProps {
  isActive: boolean;
  onActiveChange: (isActive: boolean) => void;
}

/**
 * Place info on one map: the switch beside the zoom buttons, the hint while it is on, and a popup
 * for each clicked point. `MapLibreMapHost` renders it inside every interactive map.
 */
export const MapLibrePlaceInfo: FC<IMapLibrePlaceInfoProps> = ({
  isActive,
  onActiveChange,
}) => {
  const {
    lookup,
    popupKey,
    isLoading,
    isHintShown,
    handlePointClick,
    toggle,
    retry,
    closePopup,
  } = useMapLibrePlaceInfo({ isActive, onActiveChange });

  return (
    <>
      <MapLibrePlaceInfoToggle isActive={isActive} onToggle={toggle} />

      {isHintShown && <MapLibrePlaceInfoHint />}

      {lookup && (
        <>
          <Marker
            latitude={lookup.coordinate.lat}
            longitude={lookup.coordinate.lng}
            anchor="center"
            onClick={handlePointClick}
          >
            <MapLibrePlaceInfoPointStyled $isLoading={isLoading} />
          </Marker>

          <MapLibreAutoPanPopup
            key={popupKey}
            isOpen
            latitude={lookup.coordinate.lat}
            longitude={lookup.coordinate.lng}
            offset={MAPLIBRE_PLACE_INFO.popupOffsetPx}
            maxWidth={MAPLIBRE_PLACE_INFO.popupMaxWidth}
            className="maplibre-place-info-popup"
            contentKey={lookup.status}
            shouldCloseOnMapClick={false}
            onClose={closePopup}
          >
            <MapLibrePlaceInfoCard
              lookup={lookup}
              onRetry={retry}
              onStationAdded={closePopup}
            />
          </MapLibreAutoPanPopup>
        </>
      )}
    </>
  );
};
